import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  Package,
  Shirt,
  Radio,
  Search as SearchIcon,
  Shield,
  Flame,
  Plus,
  Edit2,
  Trash2,
  ArrowDownToLine,
  ArrowUpFromLine,
  Users,
  AlertTriangle,
  CheckCircle2,
  QrCode,
  BarChart3,
  X,
  Filter,
  Download,
  ClipboardList
} from 'lucide-react';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || '';

// Icônes par catégorie
const CATEGORY_ICONS = {
  tenue: Shirt,
  communication: Radio,
  detection: SearchIcon,
  protection: Shield,
  incendie_ssiap: Flame,
  accessoire: Package
};

const CATEGORY_LABELS = {
  tenue: 'Tenues',
  communication: 'Communication',
  detection: 'Détection',
  protection: 'Protection',
  incendie_ssiap: 'Incendie/SSIAP',
  accessoire: 'Accessoires'
};

const CATEGORY_COLORS = {
  tenue: 'blue',
  communication: 'purple',
  detection: 'orange',
  protection: 'gray',
  incendie_ssiap: 'red',
  accessoire: 'green'
};

const ETAT_LABELS = {
  neuf: { label: 'Neuf', color: 'green' },
  bon: { label: 'Bon', color: 'blue' },
  usage: { label: 'Usagé', color: 'yellow' },
  a_reparer: { label: 'À réparer', color: 'orange' },
  hors_service: { label: 'Hors service', color: 'red' }
};

const Logistique = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inventaire');
  
  // Data
  const [equipements, setEquipements] = useState([]);
  const [attributions, setAttributions] = useState([]);
  const [stats, setStats] = useState(null);
  const [catalogues, setCatalogues] = useState(null);
  const [agents, setAgents] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockBasOnly, setStockBasOnly] = useState(false);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAttributionModal, setShowAttributionModal] = useState(false);
  const [showMouvementModal, setShowMouvementModal] = useState(false);
  const [selectedEquipement, setSelectedEquipement] = useState(null);
  
  // Form
  const [newEquipement, setNewEquipement] = useState({
    nom: '',
    categorie: 'tenue',
    type_equipement: '',
    marque: '',
    modele: '',
    numero_serie: '',
    taille: '',
    couleur: '',
    quantite: 1,
    quantite_min_alerte: 2,
    prix_unitaire: 0,
    fournisseur: '',
    emplacement_stockage: '',
    etat: 'neuf',
    notes: ''
  });
  
  const [newAttribution, setNewAttribution] = useState({
    equipementId: '',
    agentName: '',
    quantite: 1,
    motif: '',
    notes: ''
  });

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [eqRes, attrRes, statsRes, catRes, agentsRes] = await Promise.all([
        fetch(`${API_URL}/api/logistique/equipements`, { headers }),
        fetch(`${API_URL}/api/logistique/attributions?statut=en_cours`, { headers }),
        fetch(`${API_URL}/api/logistique/equipements/stats`, { headers }),
        fetch(`${API_URL}/api/logistique/catalogues`, { headers }),
        fetch(`${API_URL}/api/agents/`, { headers })
      ]);

      if (eqRes.ok) setEquipements(await eqRes.json());
      if (attrRes.ok) setAttributions(await attrRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (catRes.ok) setCatalogues(await catRes.json());
      if (agentsRes.ok) setAgents(await agentsRes.json());
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEquipement = async () => {
    if (!newEquipement.nom || !newEquipement.categorie) {
      toast({ title: 'Erreur', description: 'Nom et catégorie requis', variant: 'destructive' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/logistique/equipements`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newEquipement)
      });

      if (response.ok) {
        toast({ title: 'Succès', description: 'Équipement créé avec succès' });
        setShowCreateModal(false);
        resetForm();
        loadData();
      } else {
        const err = await response.json();
        toast({ title: 'Erreur', description: err.detail || 'Erreur création', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur réseau', variant: 'destructive' });
    }
  };

  const handleCreateAttribution = async () => {
    if (!newAttribution.equipementId || !newAttribution.agentName) {
      toast({ title: 'Erreur', description: 'Équipement et agent requis', variant: 'destructive' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/logistique/attributions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAttribution)
      });

      if (response.ok) {
        toast({ title: 'Succès', description: 'Équipement attribué avec succès' });
        setShowAttributionModal(false);
        setNewAttribution({ equipementId: '', agentName: '', quantite: 1, motif: '', notes: '' });
        loadData();
      } else {
        const err = await response.json();
        toast({ title: 'Erreur', description: err.detail || 'Erreur attribution', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur réseau', variant: 'destructive' });
    }
  };

  const handleRetour = async (attributionId) => {
    try {
      const response = await fetch(`${API_URL}/api/logistique/attributions/${attributionId}/retour`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ etat_retour: 'bon' })
      });

      if (response.ok) {
        toast({ title: 'Succès', description: 'Retour enregistré' });
        loadData();
      }
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur retour', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setNewEquipement({
      nom: '',
      categorie: 'tenue',
      type_equipement: '',
      marque: '',
      modele: '',
      numero_serie: '',
      taille: '',
      couleur: '',
      quantite: 1,
      quantite_min_alerte: 2,
      prix_unitaire: 0,
      fournisseur: '',
      emplacement_stockage: '',
      etat: 'neuf',
      notes: ''
    });
  };

  const filteredEquipements = equipements.filter(eq => {
    if (searchTerm && !eq.nom.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !eq.marque?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (categoryFilter && eq.categorie !== categoryFilter) return false;
    if (stockBasOnly && eq.quantite_disponible > eq.quantite_min_alerte) return false;
    return true;
  });

  const getCategoryIcon = (cat) => {
    const Icon = CATEGORY_ICONS[cat] || Package;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📦 Logistique & Matériel</h1>
          <p className="text-gray-500">Gestion du stock équipements sécurité et SSIAP</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowAttributionModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <Users className="w-4 h-4" />
            <span>Attribuer</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Nouvel Équipement</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Références</p>
              <p className="text-2xl font-bold">{stats?.total_references || 0}</p>
            </div>
            <Package className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Items</p>
              <p className="text-2xl font-bold">{stats?.total_items || 0}</p>
            </div>
            <BarChart3 className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Disponibles</p>
              <p className="text-2xl font-bold text-green-600">{stats?.total_disponible || 0}</p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Attribués</p>
              <p className="text-2xl font-bold text-orange-600">{stats?.total_attribue || 0}</p>
            </div>
            <Users className="w-8 h-8 text-orange-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Stock Bas</p>
              <p className="text-2xl font-bold text-red-600">{stats?.stock_bas_count || 0}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Valeur</p>
              <p className="text-2xl font-bold text-green-600">{(stats?.valeur_totale || 0).toLocaleString('fr-FR')}€</p>
            </div>
            <Package className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Alertes Stock Bas */}
      {stats?.stock_bas?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-semibold text-red-700">Stock bas - Réapprovisionnement nécessaire</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.stock_bas.map(item => (
              <span key={item.id} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                {item.nom} ({item.quantite} restant)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { id: 'inventaire', label: 'Inventaire', icon: Package },
          { id: 'attributions', label: 'Attributions', icon: Users },
          { id: 'categories', label: 'Par Catégorie', icon: Filter }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      {activeTab === 'inventaire' && (
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-64">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher un équipement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="">Toutes catégories</option>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <label className="flex items-center space-x-2 px-3 py-2 bg-white border rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={stockBasOnly}
              onChange={(e) => setStockBasOnly(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Stock bas uniquement</span>
          </label>
        </div>
      )}

      {/* Inventaire */}
      {activeTab === 'inventaire' && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Équipement</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taille</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disponible</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">État</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEquipements.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    Aucun équipement trouvé. Cliquez sur "Nouvel Équipement" pour commencer.
                  </td>
                </tr>
              ) : (
                filteredEquipements.map(eq => (
                  <tr key={eq.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg bg-${CATEGORY_COLORS[eq.categorie]}-100`}>
                          {getCategoryIcon(eq.categorie)}
                        </div>
                        <div>
                          <p className="font-medium">{eq.nom}</p>
                          {eq.marque && <p className="text-xs text-gray-500">{eq.marque} {eq.modele}</p>}
                          <p className="text-xs font-mono text-gray-400">{eq.code_barre}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 bg-${CATEGORY_COLORS[eq.categorie]}-100 text-${CATEGORY_COLORS[eq.categorie]}-700 text-xs rounded-full`}>
                        {CATEGORY_LABELS[eq.categorie]}
                      </span>
                    </td>
                    <td className="px-4 py-3">{eq.taille || '-'}</td>
                    <td className="px-4 py-3 font-medium">{eq.quantite}</td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${eq.quantite_disponible <= eq.quantite_min_alerte ? 'text-red-600' : 'text-green-600'}`}>
                        {eq.quantite_disponible}
                      </span>
                      {eq.quantite_disponible <= eq.quantite_min_alerte && (
                        <AlertTriangle className="w-4 h-4 text-red-500 inline ml-1" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 bg-${ETAT_LABELS[eq.etat]?.color}-100 text-${ETAT_LABELS[eq.etat]?.color}-700 text-xs rounded-full`}>
                        {ETAT_LABELS[eq.etat]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">{eq.prix_unitaire?.toFixed(2)}€</td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedEquipement(eq);
                            setNewAttribution({ ...newAttribution, equipementId: eq.id });
                            setShowAttributionModal(true);
                          }}
                          className="p-1 text-green-600 hover:text-green-800"
                          title="Attribuer"
                        >
                          <ArrowUpFromLine className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1 text-blue-600 hover:text-blue-800"
                          title="QR Code"
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Attributions */}
      {activeTab === 'attributions' && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold">Équipements actuellement attribués</h3>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Équipement</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantité</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Attribution</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motif</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {attributions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    Aucune attribution en cours
                  </td>
                </tr>
              ) : (
                attributions.map(attr => (
                  <tr key={attr.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium">{attr.equipementNom}</p>
                      <p className="text-xs text-gray-500">{attr.equipementCategorie}</p>
                    </td>
                    <td className="px-4 py-3 font-medium">{attr.agentName}</td>
                    <td className="px-4 py-3">{attr.quantite}</td>
                    <td className="px-4 py-3">{new Date(attr.date_attribution).toLocaleDateString('fr-FR')}</td>
                    <td className="px-4 py-3">{attr.motif || '-'}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleRetour(attr.id)}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center space-x-1"
                      >
                        <ArrowDownToLine className="w-4 h-4" />
                        <span>Retour</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Par Catégorie */}
      {activeTab === 'categories' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(stats?.par_categorie || {}).map(([cat, data]) => (
            <div key={cat} className={`bg-white rounded-xl p-4 shadow-sm border-l-4 border-${CATEGORY_COLORS[cat]}-500`}>
              <div className="flex items-center space-x-3 mb-3">
                <div className={`p-2 rounded-lg bg-${CATEGORY_COLORS[cat]}-100`}>
                  {getCategoryIcon(cat)}
                </div>
                <h3 className="font-semibold">{CATEGORY_LABELS[cat]}</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-2xl font-bold">{data.count}</p>
                  <p className="text-xs text-gray-500">Réf.</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{data.quantite}</p>
                  <p className="text-xs text-gray-500">Items</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{data.valeur?.toLocaleString('fr-FR')}€</p>
                  <p className="text-xs text-gray-500">Valeur</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Nouvel Équipement</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    value={newEquipement.nom}
                    onChange={(e) => setNewEquipement({ ...newEquipement, nom: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Ex: Polo Sécurité Noir"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                  <select
                    value={newEquipement.categorie}
                    onChange={(e) => setNewEquipement({ ...newEquipement, categorie: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <input
                    type="text"
                    value={newEquipement.type_equipement}
                    onChange={(e) => setNewEquipement({ ...newEquipement, type_equipement: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="polo, pantalon, talkie..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                  <input
                    type="text"
                    value={newEquipement.marque}
                    onChange={(e) => setNewEquipement({ ...newEquipement, marque: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
                  <input
                    type="text"
                    value={newEquipement.modele}
                    onChange={(e) => setNewEquipement({ ...newEquipement, modele: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taille</label>
                  <select
                    value={newEquipement.taille}
                    onChange={(e) => setNewEquipement({ ...newEquipement, taille: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Sélectionner...</option>
                    {catalogues?.tailles?.vetements?.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                  <input
                    type="text"
                    value={newEquipement.couleur}
                    onChange={(e) => setNewEquipement({ ...newEquipement, couleur: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Noir, Bleu marine..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
                  <input
                    type="number"
                    min="1"
                    value={newEquipement.quantite}
                    onChange={(e) => setNewEquipement({ ...newEquipement, quantite: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Seuil alerte stock</label>
                  <input
                    type="number"
                    min="0"
                    value={newEquipement.quantite_min_alerte}
                    onChange={(e) => setNewEquipement({ ...newEquipement, quantite_min_alerte: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix unitaire (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newEquipement.prix_unitaire}
                    onChange={(e) => setNewEquipement({ ...newEquipement, prix_unitaire: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">État</label>
                  <select
                    value={newEquipement.etat}
                    onChange={(e) => setNewEquipement({ ...newEquipement, etat: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {Object.entries(ETAT_LABELS).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emplacement stockage</label>
                  <input
                    type="text"
                    value={newEquipement.emplacement_stockage}
                    onChange={(e) => setNewEquipement({ ...newEquipement, emplacement_stockage: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Armoire A, Étagère 2..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newEquipement.notes}
                    onChange={(e) => setNewEquipement({ ...newEquipement, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="2"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateEquipement}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Créer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Attribution */}
      {showAttributionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Attribuer un Équipement</h2>
                <button onClick={() => setShowAttributionModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Équipement *</label>
                <select
                  value={newAttribution.equipementId}
                  onChange={(e) => setNewAttribution({ ...newAttribution, equipementId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Sélectionner...</option>
                  {equipements.filter(e => e.quantite_disponible > 0).map(eq => (
                    <option key={eq.id} value={eq.id}>
                      {eq.nom} {eq.taille ? `(${eq.taille})` : ''} - {eq.quantite_disponible} dispo
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agent *</label>
                <select
                  value={newAttribution.agentName}
                  onChange={(e) => setNewAttribution({ ...newAttribution, agentName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Sélectionner...</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.name}>{agent.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantité</label>
                  <input
                    type="number"
                    min="1"
                    value={newAttribution.quantite}
                    onChange={(e) => setNewAttribution({ ...newAttribution, quantite: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motif</label>
                  <select
                    value={newAttribution.motif}
                    onChange={(e) => setNewAttribution({ ...newAttribution, motif: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="permanent">Attribution permanente</option>
                    <option value="mission">Mission ponctuelle</option>
                    <option value="ronde">Ronde</option>
                    <option value="evenement">Événement</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={newAttribution.notes}
                  onChange={(e) => setNewAttribution({ ...newAttribution, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowAttributionModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateAttribution}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Attribuer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logistique;
